import { motion } from 'framer-motion';
import styled, { css } from 'styled-components';

const enhancedLayoutStyles = css`
  max-width: calc(100vw - 32px);
  align-items: stretch;
  padding: 32px 28px 28px;
  outline: none;
  border-radius: 20px;
  border: 1px solid ${({ theme }) => theme.color.brightTurquoise};
  box-shadow: 0 12px 32px rgba(48, 70, 89, 0.14);
`;

export const Wrapper = styled(motion.div)`
  width: 100vw;
  height: 100svh;
  position: fixed;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme, darkBackground }) =>
    darkBackground ? theme.color.darkGrey : 'transparent'};
  z-index: 10000000000000000;
  left: 0;
  top: 0;
`;

export const StyledPopup = styled(motion.div)`
  width: 334px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  background: ${({ theme }) => theme.color.white};
  padding: 30px 0 20px 0;
  border-radius: 8px;
  box-shadow: 0 -1px 1px 0 rgba(0, 0, 0, 0.02), 0 -1px 6px 0 rgba(0, 0, 0, 0.06);

  ${({ $isEnhancedLayout }) =>
    $isEnhancedLayout &&
    css`
      width: 440px;
      ${enhancedLayoutStyles};
    `}

  ${({ popupStyles }) => popupStyles};

  & > button:first-of-type {
    position: absolute;
    right: 13px;
    top: 13px;
  }
`;

export const Header = styled.h1`
  color: ${({ theme }) => theme.color.pickledBluewood};
  font-size: 16px;
  text-align: center;
  font-weight: bold;
  padding: 0 30px 20px 30px;
`;

export const Message = styled.p`
  white-space: pre-wrap;
  line-height: 20px;
  text-align: center;
  background: none;
  padding: 0 20px 30px 20px;
  font-weight: bold;
  font-size: 16px;
  ${({ theme }) => `
    color: ${theme.color.shark};
  `};

  ${({ $isEnhancedLayout, theme }) =>
    $isEnhancedLayout &&
    css`
      text-align: left;
      padding: 0;
      margin: 0 0 8px;
      font-size: 18px;
      font-weight: 700;
      line-height: 26px;
      letter-spacing: -0.2px;
      color: ${theme.color.pickledBluewood};
    `}
`;

export const ButtonContainer = styled.div`
  display: grid;
  grid-row-gap: 10px;
  width: 100%;
  justify-items: center;
  ${({ buttonContainerStyles }) => buttonContainerStyles};
`;

export const ContentSlot = styled.div`
  width: 100%;
  padding: 16px 0 24px;
`;

export const DialogTextArea = styled.textarea`
  display: block;
  width: 100%;
  min-height: 108px;
  box-sizing: border-box;
  resize: none;
  border: 1.5px solid ${({ theme }) => theme.color.porcelain};
  border-radius: 12px;
  outline: none;
  padding: 14px 16px;
  font-family: 'Open Sans', sans-serif;
  font-size: 15px;
  font-weight: 400;
  line-height: 22px;
  color: ${({ theme }) => theme.color.pickledBluewood};
  background-color: ${({ theme }) => theme.color.blackSqueeze};
  transition: border-color 0.18s ease, box-shadow 0.18s ease,
    background-color 0.18s ease;

  &::placeholder {
    color: ${({ theme }) => theme.color.baliHai};
    font-weight: 400;
  }

  &:hover {
    border-color: ${({ theme }) => theme.color.casper};
  }

  &:focus {
    border-color: ${({ theme }) => theme.color.brightTurquoise};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.color.brightTurquoise}22;
  }
`;

export const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  padding: 16px 0 24px;
  margin: 0;
`;

export const RadioOption = styled.label<{ $checked?: boolean }>`
  display: flex;
  align-items: center;
  gap: 14px;
  min-height: 52px;
  padding: 14px 16px;
  cursor: pointer;
  border-radius: 12px;
  border: 1.5px solid
    ${({ theme, $checked }) =>
      $checked ? theme.color.brightTurquoise : theme.color.porcelain};
  background-color: ${({ theme, $checked }) =>
    $checked ? theme.color.aquaSqueeze : theme.color.white};
  transition: background-color 0.18s ease, border-color 0.18s ease;

  &:hover {
    background-color: ${({ theme, $checked }) =>
      $checked ? theme.color.aquaSqueeze : theme.color.blackSqueeze};
    border-color: ${({ theme }) => theme.color.brightTurquoise};
  }
`;

export const RadioInput = styled.input`
  position: absolute;
  opacity: 0;
  pointer-events: none;
`;

export const RadioMark = styled.span`
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid ${({ theme }) => theme.color.casper};
  box-sizing: border-box;
  position: relative;
  transition: border-color 0.18s ease;

  ${RadioInput}:checked + & {
    border-color: ${({ theme }) => theme.color.brightTurquoise};
  }

  ${RadioInput}:checked + &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${({ theme }) => theme.color.brightTurquoise};
    transform: translate(-50%, -50%);
  }
`;

export const RadioLabel = styled.span`
  color: ${({ theme }) => theme.color.pickledBluewood};
  font-size: 15px;
  font-weight: 400;
  line-height: 22px;
`;

export const FooterActions = styled.div<{ $stack?: boolean }>`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding-top: 4px;
  flex-wrap: wrap;

  ${({ $stack }) =>
    $stack &&
    css`
      flex-direction: column-reverse;
      align-items: stretch;
      gap: 12px;

      button {
        width: 100%;
      }
    `}

  .btn-text {
    min-width: 0;
    padding: 0 12px;
    border-color: transparent;
    background-color: transparent;
    color: ${({ theme }) => theme.color.pickledBluewood};

    &:hover:enabled,
    &:focus:enabled,
    &:active:enabled {
      border-color: transparent;
      background-color: ${({ theme }) => theme.color.blackSqueeze};
      color: ${({ theme }) => theme.color.pickledBluewood};
    }
  }
`;

export const selectionActionButtonStyles = {
  width: 'auto',
  minWidth: '110px',
  padding: '0 24px',
};
